import { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  TextField,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Chat, Send } from "@mynaui/icons-react";
import assetService from "../../../services/assetService";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const getRoleBadgeColor = (role = "") => {
  const r = role.toLowerCase();
  if (r.includes("supervisor")) return { bg: "#DCFCE7", color: "#15803D" };
  if (r.includes("technician")) return { bg: "#DBEAFE", color: "#1D4ED8" };
  return { bg: "#F3F4F6", color: "#4B5563" };
};

const CommentsSection = ({ assetId, comments, setComments, currentUser }) => {
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
          await assetService.addComment(assetId, {
      assetId: parseInt(assetId),
      workOrderId: null,
      commentText: newComment.trim(),
      createdBy: currentUser?.username ?? currentUser?.email ?? "",
      createdByRole: currentUser?.role ?? "User",
    });

      // Refresh comments after posting
      const res = await assetService.getComments(assetId);
      setComments(res.data.data.comments ?? []);
      setNewComment("");
    } catch (err) {
      console.error("Failed to post comment", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chat size={20} color="#020618" strokeWidth={1.5} />
          <Typography
            sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "28px", color: "#111827" }}
          >
            Comments
          </Typography>
        </Box>
        <Typography
          sx={{ fontWeight: 400, fontSize: "14px", lineHeight: "20px", color: "#62748E" }}
        >
          All users in the loop would be notified
        </Typography>
      </Box>

      {/* Comment list */}
      {comments.length === 0 ? (
        <Typography
          sx={{ fontSize: "0.875rem", color: "#9CA3AF", py: 3, textAlign: "center" }}
        >
          No comments yet. Be the first to add one.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {comments.map((comment, index) => {
            const badgeColor = getRoleBadgeColor(comment.createdByRole ?? comment.role ?? "");
            const author = comment.createdBy ?? "Unknown";
            const role = comment.createdByRole ?? comment.role ?? "";
            const text = comment.commentText ?? comment.text ?? "";
            const time = comment.timeAgo ?? comment.createdAt ?? "";

            return (
              <Box key={comment.id ?? index}>
                <Box sx={{ display: "flex", gap: 1.5, py: 2 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      bgcolor: "#E5E7EB",
                      color: "#4B5563",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(author)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          sx={{ fontWeight: 600, fontSize: "14px", color: "#020618" }}
                        >
                          {author}
                        </Typography>
                        {role && (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: "4px",
                              bgcolor: badgeColor.bg,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "11px", fontWeight: 500, color: badgeColor.color }}
                            >
                              {role}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Typography
                        sx={{ fontWeight: 400, fontSize: "12px", color: "#62748E" }}
                      >
                        {time}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ fontWeight: 400, fontSize: "14px", color: "#374151", mt: 0.5 }}
                    >
                      {text}
                    </Typography>
                  </Box>
                </Box>
                {index < comments.length - 1 && (
                  <Divider sx={{ borderColor: "#E2E8F0" }} />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Add comment input */}
      <Divider sx={{ borderColor: "#E2E8F0", my: 2 }} />
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            fontSize: "0.875rem",
            fontWeight: 600,
            bgcolor: "#1B2A3D",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {getInitials(currentUser?.username ?? currentUser?.name ?? "U")}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "14px",
                color: "#374151",
                "& fieldset": { borderColor: "#E2E8F0" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
              },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={handleSubmit}
              disabled={submitting || !newComment.trim()}
              startIcon={
                submitting
                  ? <CircularProgress size={14} />
                  : <Send size={16} color="#62748E" strokeWidth={1.5} />
              }
              sx={{
                color: "#62748E",
                fontWeight: 400,
                fontSize: "14px",
                textTransform: "none",
              }}
            >
              {submitting ? "Posting..." : "Add comment"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CommentsSection;